import { IlogisticServiceTrelloCardData } from '../../../../../../../types/services';
import SelectableToolCard from '../../../../../../../components/SelectableToolCard/SelectableToolCard';
import NudosButton from 'components/NudosComponents/NudosButton/NudosButton';
import { XSign } from 'assets/images/components/NudosComponents';
import NudosDatePicker from 'components/NudosComponents/NudosDatePicker/NudosDatePicker';
import useDeliveredToolsState, { SECTIONS_STEPS } from '../../../../../../../state/useDeliveredToolsState';
import { LogisticsOriginAndDestinationInfo } from '../LogisticsOriginAndDestinationInfo/LogisticsOriginAndDestinationInfo';

import './SelectedToolsSection.scss';

interface SelectedToolsSectionProps {
  closeModalCallback: () => void;
  cardData: IlogisticServiceTrelloCardData;
  isUpdateTools?: boolean;
  isLoading?: boolean;
}

const SelectedToolsSection = ({
  closeModalCallback,
  cardData,
  isUpdateTools = false,
  isLoading = false
}: SelectedToolsSectionProps) => {
  const {
    deliveryDate,
    setDeliveryDate,
    setCurrentStep,
    availableTools,
    getSelectedToolsArray,
    getUnselectedToolsArray,
    selectAll,
    toggleSelectAll,
    toggleToolSelection
  } = useDeliveredToolsState();

  const selectedTools = getSelectedToolsArray();
  const unselectedTools = getUnselectedToolsArray();

  const handleGoToAddAdditionalTools = () => setCurrentStep(SECTIONS_STEPS.ADD_ADDITIONAL_TOOLS);
  const handleNextStep = () => setCurrentStep(SECTIONS_STEPS.CONFIRMATION);
  const onToolSelection = (index: number) => toggleToolSelection(index);
  const onSelectAll = () => toggleSelectAll();

  const getContent = (isUpdateTools: boolean) => {
    if (isUpdateTools) {
      return {
        sectionTitle: 'Actualizar herramientas',
        additionalToolsButton: {
          text: 'Llegarán otras herramientas'
        },
        selectedToolsTitle: `Selecciona las herramientas que serán entregadas en el servicio`,
        nextButton: {
          text: 'Siguiente',
          action: handleNextStep,
          disabledCondition: () => false,
          isLoading: isLoading
        },
        backButton: {
          text: 'Volver',
          action: closeModalCallback
        }
      };
    }

    return {
      sectionTitle: 'Pasar a entregado',
      additionalToolsButton: {
        text: 'Llegaron otras herramientas'
      },
      selectedToolsTitle: `Selecciona las herramientas que fueron entregadas en el servicio`,
      nextButton: {
        text: 'Siguiente',
        action: handleNextStep,
        disabledCondition: () => deliveryDate?.length === 0,
        isLoading: isLoading
      },
      backButton: {
        text: 'Volver',
        action: closeModalCallback
      }
    };
  };

  const content = getContent(isUpdateTools);

  return (
    <div className="selectedToolsSection">
      <div className="exitButton" onClick={closeModalCallback}>
        <XSign fill={'#B3B3B3'} />
      </div>
      <h1 className="title">{content.sectionTitle}</h1>
      {!isUpdateTools && <LogisticsOriginAndDestinationInfo cardData={cardData} />}

      <div className="dateAndToolsActionContainer">
        {!isUpdateTools && (
          <NudosDatePicker
            componentSize="small"
            label="Fecha de entrega (obligatorio)"
            updateCurrentValueCallback={setDeliveryDate}
            isFilled={!!deliveryDate}
            defaultValue={deliveryDate}
            desiredTimeZone={-6}
          />
        )}

        <div className="toolsActionContainer">
          <p className="toolsActionText">{availableTools?.length} herramientas por entregar</p>
          <button className="additionalToolsButton" onClick={handleGoToAddAdditionalTools}>
            {content.additionalToolsButton.text}
          </button>
        </div>
      </div>

      <div className="selectedToolsContainer">
        <h1 className="selectedToolsTitle">{content.selectedToolsTitle}</h1>

        <div className="toolsContainer" style={{ height: isUpdateTools ? '365px' : '320px' }}>
          {selectedTools?.map(({ tool, originalIndex }) => (
            <SelectableToolCard
              key={`selected-${originalIndex}`}
              tool={tool}
              isSelected
              showCheckbox
              onSelectionChange={() => onToolSelection(originalIndex)}
              customClassName="borderBlack"
            />
          ))}

          {selectedTools?.length > 0 && unselectedTools?.length > 0 && <div className="toolsDivider" />}

          {unselectedTools?.map(({ tool, originalIndex }) => (
            <SelectableToolCard
              key={`unselected-${originalIndex}`}
              tool={tool}
              showCheckbox
              onSelectionChange={() => onToolSelection(originalIndex)}
              customClassName="borderGray"
            />
          ))}
        </div>
      </div>

      <div className="selectAllContainer">
        <input type="checkbox" className="selectAllCheckbox" checked={selectAll} onChange={onSelectAll} />
        <label className="selectAllLabel" onClick={onSelectAll}>
          Seleccionar todas las herramientas
        </label>
      </div>

      <div className="confirmationButtons">
        <NudosButton
          componentSize="small"
          colorPalette="secondary"
          buttonText={content.backButton.text}
          handleClick={content.backButton.action}
        />

        <NudosButton
          componentSize="small"
          buttonText={content.nextButton.text}
          handleClick={content.nextButton.action}
          isButtonDisabled={content.nextButton.disabledCondition()}
        />
      </div>
    </div>
  );
};

export default SelectedToolsSection;
